
import React, { useState, useEffect } from 'react';
import { db } from '../../firebaseConfig';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
// eslint-disable-next-line no-unused-vars
import { IoClose, IoSave, IoAlertCircle, IoCheckmarkCircle } from 'react-icons/io5';

const EditarUsuarioModal = ({ user, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        nombre: '',
        email: '',
        rol: 'residente',
        tipoResidente: 'Inquilino',
        numeroCasa: '',
        numeroCelular: '',
        usoCFDI: 'S01',
        rfidCardNumber: '',
        rfidStatus: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [unidadesCatalog, setUnidadesCatalog] = useState([]);

    // --- Tags State ---
    const [etiquetasAcceso, setEtiquetasAcceso] = useState([]);
    const [nuevaEtiqueta, setNuevaEtiqueta] = useState('');

    useEffect(() => {
        const fetchCatalog = async () => {
            if (user && user.condominioId) {
                try {
                    const snap = await getDoc(doc(db, 'condominios', user.condominioId));
                    if (snap.exists()) {
                        setUnidadesCatalog(snap.data().unidades || []);
                    }
                } catch (err) {
                    console.error("Error fetching units catalog", err);
                }
            }
        };
        fetchCatalog();
    }, [user]);

    useEffect(() => {
        if (user) {
            setFormData({
                nombre: user.nombre || '',
                email: user.email || '',
                rol: user.rol || 'residente',
                tipoResidente: user.tipoResidente || 'Inquilino',
                numeroCasa: user.numeroCasa || '',
                numeroCelular: user.numeroCelular || user.telefono || '',
                usoCFDI: (user.datosFiscales && user.datosFiscales.usoCFDI) || (user.estatusValidacion === 'pendiente' ? 'S01' : 'G03'),
                rfidCardNumber: user.rfidCardNumber || '',
                rfidStatus: user.rfidStatus || ''
            });
            setEtiquetasAcceso(user.etiquetasAcceso || []);
        }
    }, [user]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        // Validation: If card number is entered, must be exactly 10 digits
        if (formData.rfidCardNumber && formData.rfidCardNumber.length !== 10) {
            setError("El número de llavero RFID debe tener exactamente 10 dígitos numéricos.");
            setLoading(false);
            return;
        }

        try {
            const userRef = doc(db, "usuarios", user.id);
            const updatePayload = {
                nombre: formData.nombre,
                // Email is usually linked to Auth, updating here only updates DB record, not Auth login.
                // We allow it for directory correction purposes.
                email: formData.email,
                rol: formData.rol,
                tipoResidente: formData.tipoResidente,
                numeroCasa: formData.numeroCasa,
                numeroCelular: formData.numeroCelular,
                // Access Tags
                etiquetasAcceso: etiquetasAcceso,
                // Update Nested Fiscal Data
                "datosFiscales.usoCFDI": formData.usoCFDI,
                // RFID Key Fob Data
                rfidCardNumber: formData.rfidCardNumber,
                rfidStatus: formData.rfidStatus
            };
            
            if (user.estatusValidacion === 'pendiente') {
                updatePayload.estatusValidacion = 'aprobado';
            }

            await updateDoc(userRef, updatePayload);
            onSuccess();
            onClose();
        } catch (err) {
            console.error("Error updating user:", err);
            setError("Error al actualizar: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '500px', padding: '30px', borderRadius: '16px' }}>
                <button className="modal-top-close-button" onClick={onClose}><IoClose /></button>

                <h2 style={{ marginBottom: '20px', color: '#111827', fontSize: '1.5rem' }}>
                    {user?.estatusValidacion === 'pendiente' ? 'Validar Registro de Usuario' : 'Editar Usuario'}
                </h2>

                {error && (
                    <div style={{ backgroundColor: '#fee2e2', color: '#b91c1c', padding: '10px', borderRadius: '8px', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <IoAlertCircle size={20} />
                        {error}
                    </div>
                )}

                {successMessage && (
                    <div style={{ backgroundColor: '#ecfdf5', color: '#047857', padding: '10px', borderRadius: '8px', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid #a7f3d0' }}>
                        <IoCheckmarkCircle size={20} />
                        {successMessage}
                    </div>
                )}

                {/* --- PENDING RFID KEY FOB BANNER --- */}
                {user?.rfidStatus === 'pendiente' && user?.rfidCardNumber && (
                    <div style={{ backgroundColor: '#fff7ed', border: '1px solid #ffedd5', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#c2410c', fontWeight: 'bold', marginBottom: '10px' }}>
                            <IoAlertCircle size={20} />
                            Solicitud de Llavero Pendiente
                        </div>
                        <p style={{ fontSize: '0.9rem', color: '#475569', marginBottom: '12px' }}>
                            El usuario ha solicitado vincular el llavero con número de serie: <strong>#{user.rfidCardNumber}</strong>
                        </p>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                type="button"
                                className="action-btn-primary"
                                style={{ backgroundColor: '#10b981', borderColor: '#10b981', padding: '6px 12px', fontSize: '0.85rem' }}
                                onClick={async () => {
                                    try {
                                        setLoading(true);
                                        await updateDoc(doc(db, "usuarios", user.id), {
                                            rfidStatus: 'activo'
                                        });
                                        setFormData(prev => ({ ...prev, rfidStatus: 'activo' }));
                                        setSuccessMessage("¡Llavero aprobado correctamente!");
                                        setTimeout(() => {
                                            onSuccess();
                                            onClose();
                                        }, 1500);
                                    } catch (err) {
                                        setError("Error al aprobar: " + err.message);
                                    } finally {
                                        setLoading(false);
                                    }
                                }}
                            >
                                Aprobar Llavero
                            </button>
                            <button
                                type="button"
                                className="action-btn-secondary"
                                style={{ color: '#dc2626', borderColor: '#fee2e2', backgroundColor: '#fef2f2', padding: '6px 12px', fontSize: '0.85rem' }}
                                onClick={async () => {
                                    if (!window.confirm("¿Seguro que deseas rechazar y remover esta solicitud de llavero?")) return;
                                    try {
                                        setLoading(true);
                                        await updateDoc(doc(db, "usuarios", user.id), {
                                            rfidCardNumber: '',
                                            rfidStatus: ''
                                        });
                                        setFormData(prev => ({ ...prev, rfidCardNumber: '', rfidStatus: '' }));
                                        setSuccessMessage("Solicitud de llavero rechazada.");
                                        setTimeout(() => {
                                            onSuccess();
                                            onClose();
                                        }, 1500);
                                    } catch (err) {
                                        setError("Error al rechazar: " + err.message);
                                    } finally {
                                        setLoading(false);
                                    }
                                }}
                            >
                                Rechazar y Remover
                            </button>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Nombre Completo</label>
                        <input
                            type="text"
                            name="nombre"
                            className="form-input"
                            value={formData.nombre}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Email (Solo registro)</label>
                        <input
                            type="email"
                            name="email"
                            className="form-input"
                            value={formData.email}
                            onChange={handleChange}
                            readOnly
                        />
                        <small style={{ color: '#6b7280' }}>* Cambiar esto NO cambia el email de acceso (Login).</small>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <div className="form-group">
                            <label>Rol de Sistema</label>
                            <select
                                name="rol"
                                className="form-input"
                                value={formData.rol}
                                onChange={handleChange}
                            >
                                <option value="residente">Residente</option>
                                <option value="admin">Administrador</option>
                                <option value="comite">Comité</option>
                                <option value="vigilante">Vigilante</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Tipo de Residente</label>
                            <select
                                name="tipoResidente"
                                className="form-input"
                                value={formData.tipoResidente}
                                onChange={handleChange}
                            >
                                <option value="Propietario">Propietario</option>
                                <option value="Inquilino">Inquilino</option>
                            </select>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <div className="form-group">
                            <label>Unidad / Casa</label>
                            <input
                                type="text"
                                list="unidades-list"
                                name="numeroCasa"
                                className="form-input"
                                placeholder="Escribe o selecciona..."
                                value={formData.numeroCasa}
                                onChange={handleChange}
                                required={user?.estatusValidacion === 'pendiente'}
                            />
                            <datalist id="unidades-list">
                                {unidadesCatalog.map((u, i) => (
                                    <option key={i} value={u} />
                                ))}
                            </datalist>
                        </div>
                        <div className="form-group">
                            <label>Celular</label>
                            <input
                                type="text"
                                name="numeroCelular"
                                className="form-input"
                                value={formData.numeroCelular}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* --- RFID KEY FOB SECTION --- */}
                    <div className="form-group" style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '10px' }}>
                        <label>Llavero Físico (RFID / Mifare)</label>
                        {['residente', 'admin', 'comite'].includes(formData.rol) ? (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                <div>
                                    <label style={{ fontSize: '0.85em', color: '#6b7280' }}>Número de Tarjeta (10 dígitos)</label>
                                    <input
                                        type="text"
                                        name="rfidCardNumber"
                                        className="form-input"
                                        placeholder="Ej: 0007556920"
                                        value={formData.rfidCardNumber}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, ''); // Numbers only
                                            if (val.length <= 10) {
                                                setFormData(prev => ({ ...prev, rfidCardNumber: val }));
                                            }
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ fontSize: '0.85em', color: '#6b7280' }}>Estatus del Llavero</label>
                                    <select
                                        name="rfidStatus"
                                        className="form-input"
                                        value={formData.rfidStatus}
                                        onChange={(e) => setFormData(prev => ({ ...prev, rfidStatus: e.target.value }))}
                                    >
                                        <option value="">Sin registrar</option>
                                        <option value="pendiente">Pendiente de Aprobación</option>
                                        <option value="activo">Activo / Habilitado</option>
                                    </select>
                                </div>
                            </div>
                        ) : (
                            <small style={{ color: '#ef4444' }}>
                                Los llaveros físicos solo pueden asignarse a residentes, administradores y comité.
                            </small>
                        )}
                    </div>

                    {/* --- USO CFDI --- */}
                    <div className="form-group" style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '10px' }}>
                        <label>Uso de CFDI (Facturación)</label>
                        <select
                            name="usoCFDI"
                            className="form-input"
                            value={formData.usoCFDI}
                            onChange={handleChange}
                        >
                            <option value="G03">G03 - Gastos en general</option>
                            <option value="S01">S01 - Sin efectos fiscales</option>
                            <option value="I01">I01 - Construcciones</option>
                            <option value="D01">D01 - Honorarios médicos</option>
                            <option value="D02">D02 - Gastos médicos por incapacidad</option>
                            <option value="CP01">CP01 - Pagos</option>
                        </select>
                        <small style={{ color: '#6b7280' }}>* Requerido por el SAT según Régimen Fiscal.</small>
                    </div>

                    {/* --- ETIQUETAS DE ACCESO --- */}
                    <div className="form-group" style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '10px' }}>
                        <label>Etiquetas de Acceso Exclusivo (Grupos / Torres)</label>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                            {etiquetasAcceso.map((tag, idx) => (
                                <span key={idx} style={{ background: '#eef2ff', color: '#4f46e5', padding: '6px 12px', borderRadius: '20px', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '6px', border: '1px solid #c7d2fe' }}>
                                    {tag}
                                    <IoClose style={{ cursor: 'pointer', fontSize: '1.2em' }} onClick={() => setEtiquetasAcceso(etiquetasAcceso.filter((_, i) => i !== idx))} />
                                </span>
                            ))}
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <input
                                type="text"
                                className="form-input"
                                placeholder="Ej. Torre A, Edificio Norte..."
                                value={nuevaEtiqueta}
                                onChange={(e) => setNuevaEtiqueta(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        if (nuevaEtiqueta.trim() && !etiquetasAcceso.includes(nuevaEtiqueta.trim())) {
                                            setEtiquetasAcceso([...etiquetasAcceso, nuevaEtiqueta.trim()]);
                                            setNuevaEtiqueta('');
                                        }
                                    }
                                }}
                            />
                            <button
                                type="button"
                                className="action-btn-secondary"
                                onClick={() => {
                                    if (nuevaEtiqueta.trim() && !etiquetasAcceso.includes(nuevaEtiqueta.trim())) {
                                        setEtiquetasAcceso([...etiquetasAcceso, nuevaEtiqueta.trim()]);
                                        setNuevaEtiqueta('');
                                    }
                                }}
                            >
                                Añadir
                            </button>
                        </div>
                        <small style={{ color: '#6b7280' }}>* Escribe y presiona Enter para agregar. Sirve para filtrar qué amenidades pueden reservar.</small>
                    </div>

                    <div className="modal-actions" style={{ marginTop: '25px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <button type="button" className="action-btn-secondary" onClick={onClose} disabled={loading}>
                            Cancelar
                        </button>
                        <button 
                            type="submit" 
                            className="action-btn-primary" 
                            disabled={loading}
                            style={user?.estatusValidacion === 'pendiente' ? { backgroundColor: '#10b981', borderColor: '#10b981' } : {}}
                        >
                            {loading ? 'Guardando...' : (user?.estatusValidacion === 'pendiente' ? 'Aprobar Residente' : 'Guardar Cambios')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditarUsuarioModal;
